import useLogAmplitudeEventOnce from '@eversports/amplitude-react/useLogAmplitudeEventOnce'
import Box from '@eversports/klimt-primitives/legacy/Box'
import Button from '@eversports/klimt-primitives/legacy/Button'
import Heading from '@eversports/klimt-primitives/legacy/Heading'
import Stack from '@eversports/klimt-primitives/legacy/Stack'
import Text from '@eversports/klimt-primitives/legacy/Text'
import { useEffect, useReducer } from 'react'

import type { Meta } from '../../../App.types'
import MatchCard, { MatchSkeleton } from '../../../components/MatchCard'
import MetaHelmet from '../../../components/MetaHelmet'
import { SportCategory, useVenueMatchesVenueQuery } from '../../../graphql'
import useMatches from '../../../hooks/useMatches'
import { Localized } from '../../../localization/react'
import getTimeRangeForDate from '../../../utils/get-time-range-for-date'
import type { Venue } from '../VenueProfile.types'
import VenueProfileContentContainer from '../VenueProfileContentContainer'

import { MatchFilters, NoMatchesFound } from './Matches'
import CreateYourMatchCard from './Matches/CreateYourMatchCard'
import matchesFiltersReducer from './Matches/helpers/matches-filters-reducer'
import { matchesFiltersInitialState, NUMBER_OF_INITIAL_MATCHES_FETCHED } from './Matches/Matches.constants'

interface Props {
  venue: Venue
  meta: Meta
}

const VenueMatches = ({ venue, meta }: Props) => {
  const { id: venueId } = venue
  const { amplitude, logAmplitudeEventOnce } = useLogAmplitudeEventOnce()
  const { matches, filterMatches, loadMoreMatches, loading, hasMoreResults } = useMatches({
    initialFilters: { venueId },
  })
  const { data: venueData } = useVenueMatchesVenueQuery({ variables: { venueId } })
  const [appliedFilters, dispatch] = useReducer(matchesFiltersReducer, matchesFiltersInitialState)
  const sports = venueData?.venue.sports.filter((sport) => sport.category !== SportCategory.FITNESS)

  useEffect(() => {
    if (appliedFilters.date === null && appliedFilters.sports === undefined) return
    void filterMatches({
      sportIds: appliedFilters.sports?.length === 0 ? undefined : appliedFilters.sports,
      timeRange: getTimeRangeForDate(appliedFilters.date),
      venueId,
    })
  }, [appliedFilters])

  useEffect(() => {
    if (!matches || !amplitude) return
    logAmplitudeEventOnce({
      eventName: 'Viewed Venue Profile Matches',
      eventProperties: {
        venue: {
          numberOfMatches: matches.length,
        },
      },
    })
  }, [amplitude, matches])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleFetchMoreMatches = () => {
    void loadMoreMatches({
      sportIds: appliedFilters.sports?.length === 0 ? undefined : appliedFilters.sports,
      timeRange: getTimeRangeForDate(appliedFilters.date),
      venueId,
    })
  }

  return (
    <>
      <MetaHelmet noIndex meta={meta} />
      <Stack sx={{ backgroundColor: 'limelite' }}>
        <MatchFilters dispatch={dispatch} sports={sports} appliedFilters={appliedFilters} />
        <VenueProfileContentContainer variant="fullwidth">
          <Stack gap={[2, 4]} paddingX={{ xs: 5, md: 0 }} paddingY={{ xs: 2, md: 0 }}>
            {venueData?.venue && (
              <Heading is="h1">
                <Localized id="public-matches-section-title" params={{ venueName: venueData.venue.name }} />
              </Heading>
            )}
            {loading || matches.length > 0 ? (
              <Box display="grid" gap={{ xs: 3, md: 4 }} gridTemplateColumns={{ xs: '1fr', md: 'repeat(4, 1fr)' }}>
                {loading ? (
                  <MatchSkeleton numberOfCopies={NUMBER_OF_INITIAL_MATCHES_FETCHED} />
                ) : (
                  <>
                    <CreateYourMatchCard
                      venueSlug={venue.slug}
                      sports={sports}
                      selectedDate={appliedFilters.date}
                      selectedSports={appliedFilters.sports}
                    />
                    {matches.map((match) => (
                      <MatchCard key={match.id} match={match} />
                    ))}
                  </>
                )}
              </Box>
            ) : (
              <NoMatchesFound venueSlug={venue.slug} />
            )}
          </Stack>
          {hasMoreResults && (
            <Button
              variant="tertiary"
              onClick={handleFetchMoreMatches}
              sx={{ padding: 3, margin: 'auto', width: { xs: '100%', sm: '50%' }, maxWidth: '448px' }}
            >
              <Text sx={{ fontWeight: 'bold', textTransform: 'none' }}>
                <Localized id="load-more" />
              </Text>
            </Button>
          )}
        </VenueProfileContentContainer>
      </Stack>
    </>
  )
}

export default VenueMatches
